<template>
  <div class="app-container">
    <div class="card card-custom mb-5">
      <div class="card-body">
        <!-- Timeline -->
        <div class="calendar-container" ref="scrollContainer">
          <div class="calendar-wrapper">
            <table class="calendar-table" :key="numberOfWeeks">
              <colgroup>
                <col
                  v-for="i in numberOfWeeks"
                  :key="`col-${i}`"
                  class="week-column"
                />
              </colgroup>
              <thead>
                <tr class="header-row">
                  <th v-for="i in numberOfWeeks" :key="i" class="week-header">
                    Tuần {{ i }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in sortedEvents"
                  :key="rowIndex"
                  class="calendar-row"
                >
                  <td
                    v-for="week in numberOfWeeks"
                    :key="week"
                    class="day-cell"
                  >
                    <div class="event-container">
                      <div
                        v-if="shouldShowEvent(row, week)"
                        :class="['event-card']"
                        :style="{
                          backgroundColor: row.color,
                          color: getTextColor(row.color),
                        }"
                      >
                        <div
                          v-if="week === row.startWeek"
                          class="event-content"
                        >
                          <span class="event-title">{{ row.title }}</span>
                          <div class="event-actions">
                            <button
                              @click="editModule(row)"
                              class="icon-button edit"
                            >
                              ✏️
                            </button>
                            <button
                              @click="deleteModule(row.moduleProgramMappingId)"
                              class="icon-button delete"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    programId: {
      type: [String, Number],
      default: null,
    },
    programData: {
      type: Object,
      default: () => ({}),
    },
    viewMode: {
      type: String,
      default: 'week',
    },
    numberOfWeeks: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      events: [],
      scrollAmount: 200, // Khoảng cách cuộn mỗi lần nhấn nút
      loading: false,
      columnWidth: 180, // Chiều rộng cố định cho mỗi cột (px)
    };
  },
  computed: {
    sortedEvents() {
      return [...this.events].sort((a, b) => {
        // Sort theo startWeek trước
        if (a.startWeek !== b.startWeek) {
          return a.startWeek - b.startWeek;
        }
        // Nếu cùng startWeek, sort theo duration (giảm dần)
        if (a.duration !== b.duration) {
          return a.duration - b.duration;
        }
        // Nếu cùng duration thì sort theo id
        return a.id - b.id;
      });
    },

    // Tính toán chiều rộng tổng của bảng
    tableWidth() {
      return `${this.columnWidth * this.numberOfWeeks}px`;
    },
  },
  watch: {
    programData: {
      handler(newProgramData) {
        if (newProgramData && newProgramData.modules) {
          // Map the modules from programData to your events format
          this.events = newProgramData.modules.map((module) => ({
            id: module.id,
            title: module.name,
            startWeek: this.getStartWeek(module.startAtDay) || 1,
            duration: module.totalWeek || 1,
            type: module.type || 'default-type',
            color: module.color || '#16A45A',
            moduleProgramMappingId: module.moduleProgramMappingId,
          }));
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getTextColor(backgroundColor) {
      console.log(backgroundColor);
      if (
        !backgroundColor ||
        typeof backgroundColor !== 'string' ||
        !/^#[0-9A-Fa-f]{6}$/.test(backgroundColor)
      ) {
        return '#000000';
      }
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    updatenumberOfWeeks(newnumberOfWeeks) {
      // Cập nhật số tuần hiển thị
      this.numberOfWeeks = newnumberOfWeeks;
    },
    getStartWeek(startAtDay) {
      // Nếu startAtDay là null, undefined hoặc 0, mặc định là tuần 1
      if (!startAtDay) return 1;

      // Tính tuần bằng cách chia (startAtDay - 1) cho 7 và cộng thêm 1
      // Điều này đảm bảo ngày 1-7 thuộc tuần 1, ngày 8-14 thuộc tuần 2, v.v.
      return Math.floor((startAtDay - 1) / 7) + 1;
    },
    shouldShowEvent(event, week) {
      return week >= event.startWeek && week < event.startWeek + event.duration;
    },

    // Methods được gọi từ component cha (ProgramView)
    addModule(moduleData) {
      // Thêm module mới vào danh sách
      this.events = [...this.events, moduleData];
    },

    updateModule(moduleData) {
      // Cập nhật module đã tồn tại
      const index = this.events.findIndex((e) => e.id === moduleData.id);
      if (index !== -1) {
        const updatedEvents = [...this.events];
        updatedEvents.splice(index, 1, moduleData);
        this.events = updatedEvents;
      }
    },

    editModule(module) {
      // Emit sự kiện để ProgramView biết và mở modal
      this.$emit('edit-module', module);
    },

    deleteModule(moduleProgramMappingId) {
      this.$swal({
        title: 'Xác nhận',
        text: 'Bạn có chắc chắn muốn xóa module này?',
        icon: 'warning',
        buttons: {
          cancel: {
            text: 'Hủy',
            value: false,
            visible: true,
            className: 'btn btn-secondary',
            closeModal: true,
          },
          confirm: {
            text: 'Xóa',
            value: true,
            visible: true,
            className: 'btn btn-danger',
            closeModal: true,
          },
        },
      }).then((result) => {
        if (result) {
          // Update local UI immediately
          this.events = this.events.filter(
            (e) => e.moduleProgramMappingId !== moduleProgramMappingId,
          );

          // Emit event to parent component to handle backend deletion and data sync
          this.$emit('delete-module', moduleProgramMappingId);
        }
      });
    },

    // Phương thức cuộn ngang
    scrollLeft() {
      if (this.$refs.scrollContainer) {
        const wrapper =
          this.$refs.scrollContainer.querySelector('.calendar-wrapper');
        wrapper.scrollLeft -= this.scrollAmount;
      }
    },

    scrollRight() {
      if (this.$refs.scrollContainer) {
        const wrapper =
          this.$refs.scrollContainer.querySelector('.calendar-wrapper');
        wrapper.scrollLeft += this.scrollAmount;
      }
    },

    // Phương thức lưu lịch trình
    saveSchedule() {
      this.loading = true;

      // Giả lập lưu dữ liệu
      setTimeout(() => {
        this.loading = false;
        this.$swal('Thành công!', 'Đã lưu lịch trình chương trình', 'success');
      }, 1000);
    },
    async deleteProgramModuleMappings(id) {
      try {
        this.loading = true;
        let { data } = await this.$api.delete(`ProgramProgramModule/${id}`);
        console.log(data);
      } catch (error) {
        this.$swal('Lỗi!', error, 'error');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.app-container {
  padding: 0;
}

.calendar-container {
  width: 100%;
  position: relative;
}

.calendar-wrapper {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scroll-behavior: smooth; /* Thêm hiệu ứng cuộn mượt */
}

.calendar-table {
  border-collapse: collapse;
  table-layout: fixed; /* Đảm bảo các cột có chiều rộng cố định */
  width: v-bind(
    tableWidth
  ); /* Set chiều rộng bảng dựa trên số cột và chiều rộng cột */
}

/* Định nghĩa chiều rộng cột cố định */
.week-column {
  width: v-bind(columnWidth + 'px'); /* Chiều rộng cố định cho mỗi cột */
}

.header-row th {
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  width: v-bind(columnWidth + 'px'); /* Đảm bảo header có cùng chiều rộng */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.day-cell {
  border: 1px solid #ddd;
  vertical-align: top;
  height: 40px;
  padding: 4px;
  width: v-bind(columnWidth + 'px'); /* Đảm bảo cell có cùng chiều rộng */
}

.event-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.event-card {
  padding: 4px 8px;
  font-size: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  border-radius: 2px;
}

.event-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: 500;
}

.event-actions {
  display: flex;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0; /* Ngăn không cho phần actions bị co lại */
}

.icon-button {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.icon-button:hover {
  opacity: 1;
}

.healthy-coping-event {
  background-color: #90ee90;
}

.being-active-event {
  background-color: #ffb3b3;
}

.healthy-nutrition-event {
  background-color: #ffb6c1;
}

.medicine-event {
  background-color: #ffe4e1;
}
</style>
